import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import DriverApp from './pages/DriverApp';
import DriverSignup from './pages/DriverSignup';
import DemoAccess from './pages/DemoAccess';
import AuthCallback from './pages/AuthCallback';
import TrackingPage from './pages/TrackingPage';
import MerchantOnboard from './pages/MerchantOnboard';
import MerchantDashboard from './pages/MerchantDashboard';
import AdminManagement from './pages/AdminManagement';
import './App.css';

// Protected route wrapper
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // If allowedRoles specified, check if user has required role
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect based on role
    if (user?.role === 'driver') {
      return <Navigate to="/driver" replace />;
    }
    if (user?.role === 'merchant_admin' || user?.role === 'merchant_user') {
      return <Navigate to="/merchant" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public route wrapper (redirects to appropriate app if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    // Redirect to appropriate app based on role
    if (user?.role === 'driver') {
      return <Navigate to="/driver" replace />;
    }
    if (user?.role === 'merchant_admin' || user?.role === 'merchant_user') {
      return <Navigate to="/merchant" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher']}>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/driver" 
        element={
          <ProtectedRoute allowedRoles={['driver']}>
            <DriverApp />
          </ProtectedRoute>
        } 
      />
      {/* Merchant routes */}
      <Route path="/onboard" element={<MerchantOnboard />} />
      {/* Public driver sign-up */}
      <Route path="/driver/signup" element={<DriverSignup />} />
      {/* Demo access page (public) */}
      <Route path="/demo" element={<DemoAccess />} />
      {/* Emergent Google OAuth callback */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route 
        path="/merchant" 
        element={
          <ProtectedRoute allowedRoles={['merchant_admin', 'merchant_user']}>
            <MerchantDashboard />
          </ProtectedRoute>
        } 
      />
      {/* Public tracking page - no auth required */}
      <Route path="/track/:trackingCode" element={<TrackingPage />} />
      <Route 
        path="/" 
        element={
          <PublicRoute>
            <LandingPage />
          </PublicRoute>
        } 
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
